import { injectable } from "inversify";
import { TableRepo } from "../table.repo";
import { ApiPage } from "@/core/models/page.model";
import {
  ElectronicContractModel,
  CreateElectronicContractModel,
  ViewDocumentModel,
  SendOtpModel,
  SignDocumentModel,
  ElectronicContractModelFactory,
} from "@/core/models/electronic-contract.model";
import { ApiResponse } from "@/core/models/response.model";

interface _ElectronicContractGetParams {
  search?: string;
  waterUserCode?: string;
  status?: string;
  waterFactoryId?: string;
  templateId?: string;
}

@injectable()
export class ElectronicContractRepo extends TableRepo<ElectronicContractModel> {
  constructor() {
    super("/api/ElectronicContract");
  }

  async getData(
    index: number,
    params: _ElectronicContractGetParams,
    pageSize = 10
  ): Promise<ApiPage<ElectronicContractModel> | null> {
    const response = await this.apiRepo.get<ApiPage<ElectronicContractModel>>(
      `${this.basepath}/page`,
      {
        pageSize: pageSize,
        pageIndex: index,
        ...params,
      }
    );
    if (response?.code === "00" && response.result) {
      return response.result;
    }
    return null;
  }

  async createContract(
    model: CreateElectronicContractModel
  ): Promise<ApiResponse<ElectronicContractModel> | undefined> {
    try {
      const response = await this.apiRepo.post<ElectronicContractModel>(
        `${this.basepath}/create`,
        model
      );
      return response;
    } catch (err) {
      console.log(err);
      return {
        code: "99",
        errorMessage: "",
        result: ElectronicContractModelFactory.getEmpty(),
      };
    }
  }

  async getById(id: string): Promise<ElectronicContractModel | null> {
    try {
      const response = await this.apiRepo.get<ElectronicContractModel>(
        `${this.basepath}/${id}`
      );
      if (response?.code === "00" && response.result) {
        return response.result;
      }
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async getByWaterUserCode(
    waterUserCode: string
  ): Promise<ElectronicContractModel | null> {
    try {
      const response = await this.apiRepo.get<ElectronicContractModel>(
        `${this.basepath}/by-water-user-code/${waterUserCode}`
      );
      if (response?.code === "00" && response.result) {
        return response.result;
      }
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async viewDocument(model: ViewDocumentModel): Promise<string | null> {
    try {
      const response = await this.apiRepo.post<string>(
        `${this.basepath}/view-document`,
        model
      );
      if (response?.code === "00" && response.result) {
        return response.result;
      }
      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async sendOtp(model: SendOtpModel): Promise<boolean> {
    try {
      const response = await this.apiRepo.post<boolean>(
        `${this.basepath}/send-otp`,
        model
      );
      if (response?.code === "00" && response.result) {
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  async signDocument(model: SignDocumentModel): Promise<boolean> {
    try {
      const response = await this.apiRepo.post<boolean>(
        `${this.basepath}/sign-document`,
        model
      );
      if (response?.code === "00" && response.result) {
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  }
}
